/**
 * Supplier Declaration Management Service
 */
import api from '../shared/services/api';

export interface Supplier {
  id: string;
  name: string;
  email: string;
  contact_person?: string;
  phone?: string;
  country?: string;
  created_at: string;
  declaration_count: number;
  active_declarations: number;
  expiring_soon: number;
}

export interface SupplierCreate {
  name: string;
  email: string;
  contact_person?: string;
  phone?: string;
  address?: string;
  country?: string;
}

export interface Declaration {
  id: string;
  supplier_id: string;
  supplier_name: string;
  declaration_type: string;
  product_description: string;
  hs_code?: string;
  origin_country?: string;
  valid_from?: string;
  valid_until?: string;
  status: string;
  document_name?: string;
  created_at: string;
}

export interface DeclarationCreate {
  supplier_id: string;
  declaration_type: string;
  product_description: string;
  hs_code?: string;
  origin_country?: string;
  valid_from?: string;
  valid_until?: string;
  certificate_number?: string;
}

export interface DashboardStats {
  total_suppliers: number;
  total_declarations: number;
  active_declarations: number;
  expiring_soon: number;
  expired: number;
  pending: number;
}

export interface ExtractionResult {
  declaration_type?: string;
  product_description?: string;
  hs_code?: string;
  origin_country?: string;
  valid_from?: string;
  valid_until?: string;
  certificate_number?: string;
  confidence_score: number;
  extracted_text: string;
  processing_status: string;
}

export interface DeclarationConfirm {
  declaration_type?: string;
  product_description?: string;
  hs_code?: string;
  origin_country?: string;
  valid_from?: string;
  valid_until?: string;
  certificate_number?: string;
}

export interface SearchResult {
  id: string;
  supplier_name: string;
  declaration_type: string;
  product_description: string;
  hs_code?: string;
  origin_country?: string;
  valid_until?: string;
  status: string;
  relevance_score: number;
  snippet?: string;
}

export interface EmailLog {
  id: string;
  supplier_id: string;
  supplier_name: string;
  declaration_id?: string;
  email_type: string;
  subject: string;
  body: string;
  sent_at: string;
  status: string;
  message_id?: string;
  error_message?: string;
}

export interface Alert {
  id: string;
  declaration_id: string;
  supplier_id: string;
  supplier_name: string;
  product_description: string;
  alert_type: string;
  alert_date: string;
  days_before_expiry?: number;
  sent: boolean;
  sent_at?: string;
  acknowledged: boolean;
  acknowledged_at?: string;
  valid_until?: string;
  declaration_status?: string;
}

export interface VersionHistory {
  id: string;
  declaration_id: string;
  version_number: number;
  changed_fields: Record<string, { old: any; new: any }>;
  changed_by: string;
  changed_at: string;
  change_reason?: string;
}

/**
 * Get dashboard statistics
 */
export const getDashboardStats = async (): Promise<DashboardStats> => {
  const response = await api.get('/api/v1/supplier-declarations/dashboard');
  return response.data;
};

/**
 * Create a new supplier
 */
export const createSupplier = async (supplier: SupplierCreate): Promise<Supplier> => {
  const response = await api.post('/api/v1/supplier-declarations/suppliers', supplier);
  return response.data;
};

/**
 * Update an existing supplier
 */
export const updateSupplier = async (id: string, supplier: SupplierCreate): Promise<Supplier> => {
  const response = await api.patch(`/api/v1/supplier-declarations/suppliers/${id}`, supplier);
  return response.data;
};

/**
 * Get all suppliers
 */
export const getSuppliers = async (skip = 0, limit = 50): Promise<Supplier[]> => {
  const response = await api.get('/api/v1/supplier-declarations/suppliers', {
    params: { skip, limit },
  });
  return response.data;
};

/**
 * Create a new declaration
 */
export const createDeclaration = async (declaration: DeclarationCreate): Promise<Declaration> => {
  const response = await api.post('/api/v1/supplier-declarations/declarations', declaration);
  return response.data;
};

/**
 * Get all declarations
 */
export const getDeclarations = async (
  supplierId?: string,
  status?: string,
  skip = 0,
  limit = 50
): Promise<Declaration[]> => {
  const response = await api.get('/api/v1/supplier-declarations/declarations', {
    params: { supplier_id: supplierId, status, skip, limit },
  });
  return response.data;
};

/**
 * Upload declaration document for AI extraction
 */
export const uploadDeclarationDocument = async (
  declarationId: string,
  file: File
): Promise<ExtractionResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(
    `/api/v1/supplier-declarations/declarations/${declarationId}/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * Confirm extracted declaration data
 */
export const confirmDeclarationData = async (
  declarationId: string,
  data: DeclarationConfirm
): Promise<Declaration> => {
  const response = await api.patch(
    `/api/v1/supplier-declarations/declarations/${declarationId}/confirm`,
    data
  );
  return response.data;
};

/**
 * Search declarations with full-text search
 */
export const searchDeclarations = async (query: string): Promise<SearchResult[]> => {
  if (!query || query.trim() === '') {
    return [];
  }
  const response = await api.get('/api/v1/supplier-declarations/search', {
    params: { q: query },
  });
  return response.data;
};

/**
 * Get email logs with optional filters
 */
export const getEmailLogs = async (
  supplierId?: string,
  declarationId?: string,
  emailType?: string,
  status?: string,
  skip = 0,
  limit = 50
): Promise<EmailLog[]> => {
  const response = await api.get('/api/v1/supplier-declarations/email-logs', {
    params: {
      supplier_id: supplierId,
      declaration_id: declarationId,
      email_type: emailType,
      status,
      skip,
      limit,
    },
  });
  return response.data;
};

/**
 * Get alerts with optional filters
 */
export const getAlerts = async (
  status?: string,
  acknowledged?: boolean,
  skip = 0,
  limit = 50
): Promise<Alert[]> => {
  const response = await api.get('/api/v1/supplier-declarations/alerts', {
    params: {
      status,
      acknowledged,
      skip,
      limit,
    },
  });
  return response.data;
};

/**
 * Acknowledge an alert
 */
export const acknowledgeAlert = async (alertId: string): Promise<Alert> => {
  const response = await api.post(`/api/v1/supplier-declarations/alerts/${alertId}/acknowledge`);
  return response.data;
};

/**
 * Get declaration version history
 */
export const getDeclarationHistory = async (declarationId: string): Promise<VersionHistory[]> => {
  const response = await api.get(
    `/api/v1/supplier-declarations/declarations/${declarationId}/history`
  );
  return response.data;
};

/**
 * Smart upload: Upload declaration document and auto-create supplier + declaration
 */
export const smartUploadDeclaration = async (file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(
    '/api/v1/supplier-declarations/declarations/smart-upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * Discard a draft declaration created by smart upload (called on cancel)
 */
export const discardDraftDeclaration = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/supplier-declarations/declarations/${id}/discard`);
};

/**
 * Delete a single declaration
 */
export const deleteDeclaration = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/supplier-declarations/declarations/${id}`);
};

/**
 * Bulk delete declarations
 */
export const bulkDeleteDeclarations = async (ids: string[]): Promise<{ deleted_count: number }> => {
  const response = await api.delete('/api/v1/supplier-declarations/declarations', { data: { ids } });
  return response.data;
};

/**
 * Delete a single supplier and all its declarations
 */
export const deleteSupplier = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/supplier-declarations/suppliers/${id}`);
};

/**
 * Bulk delete suppliers and all their declarations
 */
export const bulkDeleteSuppliers = async (ids: string[]): Promise<{ deleted_count: number }> => {
  const response = await api.delete('/api/v1/supplier-declarations/suppliers', { data: { ids } });
  return response.data;
};

export const sendRenewalEmail = async (declarationId: string): Promise<{ message: string }> => {
  const response = await api.post(`/api/v1/supplier-declarations/declarations/${declarationId}/send-renewal`);
  return response.data;
};

export const triggerEmailPoll = async (): Promise<{ status: string; emails_found: number; attachments_processed: number; message?: string }> => {
  const response = await api.post('/api/v1/supplier-declarations/trigger-email-poll');
  return response.data;
};

export const bulkSendRenewalEmails = async (
  ids: string[]
): Promise<{ sent: number; failed: number; errors: string[] }> => {
  const response = await api.post('/api/v1/supplier-declarations/declarations/bulk-send-renewal', { ids });
  return response.data;
};

export interface SupplierComplianceReport {
  supplier_id: string;
  supplier_name: string;
  supplier_email: string;
  total_declarations: number;
  active: number;
  expiring_soon: number;
  expired: number;
  pending: number;
  requested: number;
  compliance_score: number;
  at_risk: number;
}

export const getComplianceReport = async (): Promise<SupplierComplianceReport[]> => {
  const response = await api.get('/api/v1/supplier-declarations/compliance-report');
  return response.data;
};

const downloadBlob = (data: BlobPart, mimeType: string, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const exportComplianceReportPdf = async (): Promise<void> => {
  const response = await api.get('/api/v1/supplier-declarations/compliance-report/export/pdf', {
    responseType: 'blob',
  });
  const date = new Date().toISOString().slice(0, 10);
  downloadBlob(response.data, 'application/pdf', `compliance_report_${date}.pdf`);
};

export const exportComplianceReportExcel = async (): Promise<void> => {
  const response = await api.get('/api/v1/supplier-declarations/compliance-report/export/excel', {
    responseType: 'blob',
  });
  const date = new Date().toISOString().slice(0, 10);
  downloadBlob(
    response.data,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    `compliance_report_${date}.xlsx`
  );
};

export const bulkConfirmDeclarations = async (ids: string[]): Promise<{ confirmed: number }> => {
  const response = await api.post('/api/v1/supplier-declarations/declarations/bulk-confirm', { ids });
  return response.data;
};

export const markDeclarationReceived = async (id: string): Promise<Declaration> => {
  const response = await api.patch(`/api/v1/supplier-declarations/declarations/${id}/mark-received`);
  return response.data;
};

// ── Requirement types ────────────────────────────────────────────────────────

export interface Requirement {
  id: string;
  hs_code_pattern: string;
  origin_country: string;
  declaration_type: string;
  description?: string;
  is_mandatory: boolean;
  created_at: string;
}

export interface RequirementCreate {
  hs_code_pattern: string;
  origin_country?: string;
  declaration_type: string;
  description?: string;
  is_mandatory?: boolean;
}

export interface RequirementCheckItem {
  declaration_type: string;
  description?: string;
  is_mandatory: boolean;
  hs_code_pattern: string;
  satisfied: boolean;
  source: 'manual' | 'ai';
  declaration_id?: string;
  declaration_status?: string;
}

export interface AISuggestedRequirement {
  declaration_type: string;
  description: string;
  is_mandatory: boolean;
}

export interface AISuggestResponse {
  hs_code: string;
  origin_country?: string;
  suggestions: AISuggestedRequirement[];
}

export interface RequirementCheckResponse {
  hs_code: string;
  origin_country?: string;
  supplier_id?: string;
  supplier_name?: string;
  requirements: RequirementCheckItem[];
  total: number;
  satisfied: number;
  missing: number;
}

export const listRequirements = async (): Promise<Requirement[]> => {
  const response = await api.get('/api/v1/supplier-declarations/requirements');
  return response.data;
};

export const createRequirement = async (data: RequirementCreate): Promise<Requirement> => {
  const response = await api.post('/api/v1/supplier-declarations/requirements', data);
  return response.data;
};

export const deleteRequirement = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/supplier-declarations/requirements/${id}`);
};

export const checkRequirements = async (
  hs_code: string,
  origin_country?: string,
  supplier_id?: string,
  product_description?: string,
): Promise<RequirementCheckResponse> => {
  const response = await api.post('/api/v1/supplier-declarations/check-requirements', {
    hs_code,
    origin_country,
    supplier_id,
    product_description,
  });
  return response.data;
};

export interface InvoiceLineItem {
  product_description: string;
  hs_code?: string;
  origin_country?: string;
  quantity?: string;
  requirements: RequirementCheckItem[];
  satisfied: number;
  missing: number;
}

export interface InvoiceAnalysisResponse {
  supplier_name?: string;
  supplier_country?: string;
  invoice_number?: string;
  invoice_date?: string;
  line_items: InvoiceLineItem[];
  total_requirements: number;
  total_satisfied: number;
  total_missing: number;
}

export const analyzeInvoice = async (
  file: File,
  supplier_id?: string
): Promise<InvoiceAnalysisResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  if (supplier_id) formData.append('supplier_id', supplier_id);
  const response = await api.post(
    '/api/v1/supplier-declarations/analyze-invoice',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export interface SaveRequirementsResponse {
  created: number;
  skipped: number;
  supplier_id: string;
}

export const saveAnalysisRequirements = async (
  analysisResult: InvoiceAnalysisResponse,
  supplierId?: string
): Promise<SaveRequirementsResponse> => {
  const response = await api.post('/api/v1/supplier-declarations/save-analysis-requirements', {
    supplier_id: supplierId || null,
    supplier_name: analysisResult.supplier_name || null,
    supplier_country: analysisResult.supplier_country || null,
    line_items: analysisResult.line_items.map(item => ({
      product_description: item.product_description,
      hs_code: item.hs_code,
      origin_country: item.origin_country,
      requirements: item.requirements.map(r => ({
        declaration_type: r.declaration_type,
        is_mandatory: r.is_mandatory,
        description: r.description,
        satisfied: r.satisfied,
      })),
    })),
  });
  return response.data;
};

export const aiSuggestRequirements = async (
  hs_code: string,
  origin_country?: string,
  product_description?: string
): Promise<AISuggestResponse> => {
  const response = await api.post('/api/v1/supplier-declarations/ai-suggest-requirements', {
    hs_code,
    origin_country,
    product_description,
  });
  return response.data;
};

export default {
  getDashboardStats,
  createSupplier,
  getSuppliers,
  createDeclaration,
  getDeclarations,
  uploadDeclarationDocument,
  confirmDeclarationData,
  searchDeclarations,
  getEmailLogs,
  getAlerts,
  acknowledgeAlert,
  getDeclarationHistory,
  smartUploadDeclaration,
  deleteDeclaration,
  bulkDeleteDeclarations,
  deleteSupplier,
  bulkDeleteSuppliers,
  listRequirements,
  createRequirement,
  deleteRequirement,
  checkRequirements,
  aiSuggestRequirements,
  analyzeInvoice,
  saveAnalysisRequirements,
};
